import './SelectField.scss';
import { Select, SelectProps, Form, Input, Checkbox } from 'antd';
import React, { useMemo, useCallback } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DefaultOptionType } from 'antd/es/select';

import { Block, BlockProps, FlexBox, Spacer } from '../../layouts';
import { TextCommon, TextProps } from '../../typo';
import { FormConnector } from '../FormConnector';
import { IconCheck } from '../../icons';
import { DropdownRenderCreate } from './DropdownRenderCreate';

interface SelectFieldProps<T> extends SelectProps {
    readonly name: string;
    readonly label?: string | React.ReactNode;
    readonly contentLabelRender?: React.ReactNode;
    readonly labelStyle?: TextProps;
    readonly placeholder?: string;
    readonly rules?: UseControllerProps['rules'];
    readonly options?: DefaultOptionType[];
    readonly dataSource?: Array<T>;
    readonly dataValueKey?: keyof T;
    readonly dataLabelKey?: keyof T;
    readonly onCreate?: (value?: string) => void;
    readonly createTextCode?: string;
    readonly typeCreate?: 'input' | 'modal';
    readonly description?: string;
    readonly onChange?: SelectProps['onChange'];
    readonly addonBefore?: string;
    readonly addonBeforeWidth?: string | number;
    readonly containerStyle?: BlockProps;
}

const listToOptions = <T,>(
    list: Array<T>,
    labelKey: keyof T,
    valueKey: keyof T
) => {
    return list.map((item) => ({
        label: item[labelKey],
        value: item[valueKey]
    } as DefaultOptionType)
    );
};

export const SelectField = <T,>(props: SelectFieldProps<T>) => {
    const { t } = useTranslation();
    const { name, rules, label, placeholder, options,
        dataSource, dataLabelKey, dataValueKey, typeCreate, filterOption,
        onCreate, addonBefore, addonBeforeWidth = 10, description, createTextCode,
        labelStyle, contentLabelRender, containerStyle, ...rest } = props;

    const isRequired = !!rules?.required;

    const selectOptions = useMemo(() => {
        if (dataSource) {
            return listToOptions(
                dataSource,
                dataLabelKey!,
                dataValueKey!
            );
        }
        return options ?? [];
    }, [dataLabelKey, dataSource, dataValueKey, options]);

    const dropdownRenderCustom = useCallback((options: React.ReactElement) => {
        return (
            <DropdownRenderCreate
                options={options}
                onCreate={onCreate}
                typeCreate={typeCreate}
                createText={t(createTextCode!)}
            />
        );
    },
        [onCreate, typeCreate, t, createTextCode]
    );

    const tagRender: SelectProps['tagRender'] = (props) => {
        const { label, closable, onClose } = props;

        return (
            <FlexBox
                gap={4}
                alignItems='center'
                className='select-tab'
            >
                <TextCommon maxWidth={180} ellipsis>{label}</TextCommon>
                {closable && (
                    <span
                        onClick={onClose}
                        style={{
                            cursor: 'pointer',
                            color: '#98A2B3',
                            fontSize: 12,
                        }}
                    >
                        ×
                    </span>
                )}
            </FlexBox>
        );
    };

    const onRender = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (renderProps: any) => {
            const { onChange, value } = renderProps.field;
            const { error } = renderProps.fieldState;

            const handleChange: SelectProps['onChange'] = (value, option) => {
                onChange(value ?? null);
                props?.onChange?.(value, option);
            };

            const optionRender = (opt: DefaultOptionType) => {
                const isSelected = props.mode === 'multiple'
                    ? Array.isArray(value) && value.includes(opt.value)
                    : value === opt.value;

                if (props.mode === 'multiple') {
                    return (
                        <FlexBox gap={12}>
                            <Checkbox checked={isSelected} />
                            <TextCommon ellipsis>{opt.label}</TextCommon>
                        </FlexBox>
                    );

                }

                return <FlexBox gap={4} justifyContent='space-between'>
                    <span>{opt.label}</span>
                </FlexBox>;
            };

            return (
                <Form.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message && t(error?.message)}
                >
                    {addonBefore ?
                        <Input.Group compact>
                            <Input
                                className='input-field-addonBefore'
                                style={{ backgroundColor: '#fff', width: addonBeforeWidth, textAlign: 'center' }}
                                value={addonBefore}
                                disabled
                            />
                            <Select
                                className='select-field-addonBefore'
                                style={{ width: `calc(100% - ${addonBeforeWidth})` }}
                                filterOption={filterOption}
                                value={value}
                                dropdownRender={dropdownRenderCustom}
                                placeholder={placeholder && t(placeholder)}
                                options={selectOptions}
                                allowClear
                                optionRender={optionRender}
                                {...rest}
                                onChange={handleChange}
                            />
                        </Input.Group>
                        :
                        <Select
                            style={{ width: '100%' }}
                            filterOption={filterOption}
                            value={value}
                            dropdownRender={dropdownRenderCustom}
                            placeholder={placeholder && t(placeholder)}
                            options={selectOptions}
                            allowClear
                            tagRender={tagRender}
                            optionRender={optionRender}
                            {...rest}
                            maxTagCount={rest.maxTagCount || 5}
                            maxTagPlaceholder={(omittedValues) => (
                                <FlexBox
                                    alignItems='center'
                                >
                                    +{omittedValues.length}
                                </FlexBox>
                            )}
                            onChange={handleChange}
                            listItemHeight={42}
                            menuItemSelectedIcon={props.mode === 'multiple' ? null : <IconCheck color='#3D8CF5' />}

                        />
                    }
                </Form.Item>
            );
        },
        [addonBefore, addonBeforeWidth, dropdownRenderCustom, filterOption, placeholder, props, rest, selectOptions, t]
    );

    return (
        <FormConnector>
            {(form) => {
                const { control } = form;
                return (
                    <Block width='100%' {...containerStyle}>
                        {(label || contentLabelRender) && (
                            <Spacer bottom={6}>
                                <FlexBox justifyContent='space-between'>
                                    {label && (
                                        <Block>
                                            {typeof label === 'string' ? (
                                                <TextCommon
                                                    fontWeight={600}
                                                    lineHeight='20px'
                                                    maxWidth={100}
                                                    className='text-ellipsis'
                                                    color='#344054'
                                                    {...labelStyle}
                                                >
                                                    {t(label)}
                                                </TextCommon>
                                            ) : (
                                                <>{label}</>
                                            )}

                                            {isRequired && <span style={{ color: 'red' }}> *</span>}
                                        </Block>
                                    )}

                                    {
                                        contentLabelRender && contentLabelRender
                                    }
                                </FlexBox>
                            </Spacer>
                        )}
                        {description && (<Spacer bottom={4}><TextCommon>{description}</TextCommon></Spacer>)}
                        <Controller name={name} control={control} render={onRender} rules={rules} />
                    </Block>
                );
            }}
        </FormConnector>
    );
};

const filterOptionsDefault = (input: string, option: DefaultOptionType) => (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLocaleLowerCase());

SelectField.defaultProps = {
    dataLabelKey: 'name',
    dataValueKey: 'id',
    typeCreate: 'modal',
    createTextCode: 'Add new',
    filterOption: filterOptionsDefault
};